import { claimBasket } from "@/api/modules/basket";
import { setEmailDelivery, setSnailmailDelivery, setControlFeeDelivery } from "@/api/modules/delivery";
import { newsletterSubscribe } from "@/api/modules/customer";
import {
    payAnonymousDeveloper,
    payAnonymousFree,
    exchangeDownloadTokenDeveloper,
    exchangeDownloadTokenFree,
    finalizeNetaxept
} from "@/api/modules/confirm";

export const state = {
    productType: null,
    orderId: null,
    transactionId: null,
    priceOre: null,
    details: {},
    claimed: false,
    deliveryMethod: "email",
    downloadToken: null,
    paymentProvider: null,
    paymentProviderError: null,
    applePayToken: null
};

export const mutations = {
    reset: (state, productType) => {
        state.productType = productType;
        state.orderId = null;
        state.transactionId = null;
        state.priceOre = null;
        state.details = {};
        state.claimed = false;
        state.downloadToken = null;
        state.deliveryMethod = "email";
        state.applePayToken = null;
    },
    setPaymentProvider: (state, provider) => {
        state.paymentProvider = provider;
    },
    setOrderId: (state, orderId) => {
        state.orderId = orderId;
    },
    setPaymentProviderError: (state, code) => {
        state.paymentProviderError = code;
    },
    setTransactionId: (state, transactionId) => {
        state.transactionId = transactionId;
    },
    setPriceOre: (state, price) => {
        state.priceOre = price;
    },
    setDetails: (state, details) => {
        state.details = details;
    },
    setDownloadToken: (state, token) => {
        state.downloadToken = token;
    },
    setClaimed: (state, val) => {
        state.claimed = val;
    },
    setDeliveryMethod: (state, val) => {
        state.deliveryMethod = val;
    },
    setApplePayToken: (state, token) => {
        state.applePayToken = token;
    }

};

export const getters = {
    getProductType: state => state.productType,
    getOrderId: state => state.orderId,
    getEpayPaymentId: state => state.epayPaymentId,
    getDibsEasyPaymentId: state => state.dibsEasyPaymentId,
    getPriceOre: state => state.priceOre,
    getDetails: state => state.details,
    getDeliveryMethod: state => state.deliveryMethod,
    getDownloadToken: state => state.downloadToken,
    getPaymentProvider: state => {
        if (state.priceOre === 0) return "Free";
        return (
            state.paymentProvider
        );
    },
    getPaymentProviderError: state => state.paymentProviderError,
    getApplePayToken: state => state.applePayToken
};

export const actions = {
    prepare: (context, { productType, ...rest }) => {
        if (!productType)
            throw new Error("cannot prepare order, productType is missing");
        const module =
            context.rootGetters["system/enabledProductsModuleMapping"][
                productType
            ];
        if (!module)
            throw new Error("cannot prepare order, productType is not enabled");
        context.commit("reset", productType);
        return context.dispatch(`products/${module}/prepareOrder`, rest, {
            root: true
        });
    },
    create: context => {
        if (!context.state.productType)
            throw new Error("cannot create order because it is not prepared!");
        const module =
            context.rootGetters["system/enabledProductsModuleMapping"][
                context.state.productType
            ];
        return context.dispatch(`products/${module}/createOrder`, null, {
            root: true
        });
    },
    claim: async context => {
        const { orderId, claimed } = context.state;
        if (!orderId) throw new Error("cannot claim order, orderId is missing");
        if (claimed) throw new Error("cannot claim order, already claimed");

        const name = context.rootGetters["customer/getName"];
        const email = context.rootGetters["customer/getEmail"];

        // email delivery
        const identificationType =
            context.rootGetters["customer/getIdentificationType"];
        const controlNumbers =
            context.rootGetters["customer/getControlNumbers"];

        // snailmail delivery
        const delivery = {
            customerName: context.rootGetters["customer/getDeliveryName"],
            address: context.rootGetters["customer/getDeliveryAddress"],
            cityName: context.rootGetters["customer/getDeliveryCity"],
            zipCode: context.rootGetters["customer/getDeliveryZip"],
            countryName: context.rootGetters["customer/getDeliveryCountry"]
        };

        const performDelivery = async () => {
            if (context.state.deliveryMethod === "email") {
                const response = await setEmailDelivery(
                    orderId,
                    identificationType.id,
                    controlNumbers
                );
                if (!response.ok) throw new Error(JSON.stringify(response));
                return true;
            }

            if (context.state.deliveryMethod === "snailmail") {
                const response = await setSnailmailDelivery(orderId, delivery);
                if (!response.ok) throw new Error(JSON.stringify(response));
                context.commit("setPriceOre", response.responseBody.priceOre);
                return true;
            }

            if(context.state.deliveryMethod === "controlfee"){
                const response = await setControlFeeDelivery( 
                    orderId
                );
                if (!response.ok) throw new Error(JSON.stringify(response));
                return true;
            }

            throw new Error("unknown delivery method");
        };

        const newsletterAccept =
            context.rootGetters["customer/getNewsletterAccept"];

        await performDelivery();

        const response = await claimBasket(orderId, name, email);
        if (!response.ok) throw new Error(JSON.stringify(response));
        context.commit("setClaimed", true);

        // swallow newsletter errors, so we don't loose the customer after the basket has been claimed
        if (newsletterAccept)
            await newsletterSubscribe(email, name).catch(() => {});

        return true;
    },
    confirm: context => {
        const {
            downloadToken,
            orderId,
            transactionId,
            deliveryMethod
        } = context.state;

        const paymentProvider = context.getters.getPaymentProvider;
        if (downloadToken) return Promise.resolve();

        let paymentConfirmPromise = null;
        switch (paymentProvider) {
            case "Netaxept":
                paymentConfirmPromise = finalizeNetaxept(
                    orderId,
                    transactionId,
                    true
                );
                break;
            case "Developer":
                paymentConfirmPromise = payAnonymousDeveloper(orderId, true);
                break;
            case "Free":
                paymentConfirmPromise = payAnonymousFree(orderId, true);
                break;
            case "ApplePay":
                console.log("Paying with Apple pay");
                paymentConfirmPromise = finalizeNetaxept(
                    orderId,
                    transactionId,
                    true
                );
                break;
            default:
                return Promise.reject(
                    new Error("Unknown payment provider: " + paymentProvider)
                );
        }

        return paymentConfirmPromise
            .then(response => {
                if (response.ok) {
                    return Promise.resolve(
                        response.responseBody.downloadToken ||
                            response.responseBody.itemDownloadToken
                    );
                }

                if (
                    response.status === 400 &&
                    response.responseBody.error === "OrderIsFinalized"
                ) {
                    // When paying with MobilePay through DIBS, and the customer has the MobilePay app installed, then
                    // MobilePay will redirect back to our webshop *twice*. Both from the original browser tab, and from
                    // a new one opened by the MobilePay app. They open the new tab to "improve" usability, because they
                    // can't redirect directly back to the original browser tab from the MobilePay app. But the old tab
                    // is still alive in some self refreshing loop that also redirects back to our webshop, meaning we
                    // get two concurrent arrivals for order finalization of the same order. Our backend does not allow
                    // for this insanity, and thus throws an error. To work around this issue, we catch the error and
                    // instead call a different endpoint to exchange the order ID and transaction ID for the download
                    // token.

                    return Promise.resolve(null);
                }

                if (response.status === 422 && response.responseBody.error) {
                    throw new Error(response.responseBody.error);
                } else {
                    throw new Error(JSON.stringify(response));
                }
            })
            .then(downloadToken => {
                if(deliveryMethod === "controlfee"){
                    //We don't return token because there is nothing to download
                    return {
                        ok: true,
                        status: 200,
                        statusText: "Ok",
                        responseBody: { }
                    };
                }

                if (downloadToken || deliveryMethod !== "email") {
                    // We return the same models as the exchange download token endpoint
                    return {
                        ok: true,
                        status: 200,
                        statusText: "Ok",
                        responseBody: { downloadToken }
                    };
                }

                switch (paymentProvider) {
                    case "Developer":
                        return exchangeDownloadTokenDeveloper(orderId, true);
                    case "Free":
                        return exchangeDownloadTokenFree(orderId, true);
                    default:
                        return Promise.reject(
                            new Error(
                                "Cannot exchange download token for provider: " +
                                    paymentProvider
                            )
                        );
                }
            })
            .then(response => {
                if (response.ok) {
                    context.commit(
                        "setDownloadToken",
                        response.responseBody.downloadToken ||
                            response.responseBody.itemDownloadToken
                    );
                    return Promise.resolve(context.getters.getDownloadToken);
                }

                return Promise.reject(new Error(JSON.stringify(response)));
            })
            .catch(response => {
                if (context.getters.getDownloadToken !== null) {
                    return Promise.resolve(context.getters.getDownloadToken);
                }

                return Promise.reject(response);
            });
    }
};
